import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	FlatList,
	Modal,
	TouchableWithoutFeedback,
	View,
} from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ListRow } from '../../design/components';
import Text from '../../components/text/Text';
import TextInput from '../../components/input/TextInput';

import { normalizeBarcode } from './StocktakeDetailHelpers';
import { createStocktakeDetailStyles } from './StocktakeDetail.styles';

const ManualProductSelectorDialog = ({
	isOpen,
	onClose,
	onSelect,
	items = [],
	canEdit,
}) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (!isOpen) {
			setQuery('');
		}
	}, [isOpen]);

	const filteredItems = useMemo(() => {
		const normalizedQuery = normalizeBarcode(query).toLowerCase();

		if (!normalizedQuery) {
			return items;
		}

		return items.filter(item => {
			const name = item.warehouseProduct?.product?.name?.toLowerCase() || '';
			const barcodeMatches = (item.warehouseProduct?.product?.barcodes || [])
				.some(code => normalizeBarcode(code).toLowerCase().includes(normalizedQuery));

			return name.includes(normalizedQuery) || barcodeMatches;
		});
	}, [items, query]);

	const handleSelect = useCallback((itemId) => {
		if (!canEdit || !itemId) {
			return;
		}
		onSelect(itemId);
		onClose();
	}, [canEdit, onClose, onSelect]);

	const renderManualItem = useCallback(({ item }) => {
		const barcodes = item.warehouseProduct?.product?.barcodes?.filter(Boolean) || [];

		return (
			<ListRow
				style={styles.manualSelectItem}
				title={item.warehouseProduct?.product?.name || 'Unnamed product'}
				subtitle={`Barcodes: ${barcodes.length ? barcodes.join(', ') : '—'}`}
				onPress={() => handleSelect(item.id)}
				showChevron={false}
				disabled={!canEdit}
			/>
		);
	}, [canEdit, handleSelect, styles.manualSelectItem]);

	return (
		<Modal
			visible={isOpen}
			transparent={true}
			animationType="fade"
			onRequestClose={onClose}
		>
			<View style={styles.manualModalBackdrop}>
				<TouchableWithoutFeedback onPress={onClose}>
					<View style={styles.manualModalOverlay} />
				</TouchableWithoutFeedback>

				<View style={styles.manualModalContent}>
					<Text style={styles.dialogTitle}>
						Select product
					</Text>
					<TextInput
						value={query}
						onChange={setQuery}
						placeholder="Search by name or barcode"
						style={styles.manualSelectSearchInput}
					/>
					<FlatList
						keyboardShouldPersistTaps="handled"
						data={filteredItems}
						keyExtractor={item => item.id}
						style={styles.manualSelectList}
						renderItem={renderManualItem}
						ListEmptyComponent={(
							<Text style={styles.manualSelectEmptyText}>
								{items.length ? 'No products match this search' : 'No products available in this stocktake'}
							</Text>
						)}
					/>
				</View>
			</View>
		</Modal>
	);
};

export default ManualProductSelectorDialog;
